import { Briefcase, Calendar, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

const Experience = () => {
  const experiences = [
    {
      company: "Claro Argentina",
      role: "Frontend Developer",
      period: "2022 - Present",
      description: "Leading frontend development for enterprise delivery management platform serving thousands of users daily.",
      highlights: [
        "Architected microfrontend system using React and Module Federation, enabling independent team deployments",
        "Improved platform performance by 30% through code splitting and optimized state management with Zustand",
        "Collaborated with backend teams to integrate legacy systems while maintaining modern architecture patterns",
        "Scaled platform to handle 5,000+ daily operations with real-time updates and coordination"
      ],
      technologies: ["React", "TypeScript", "Zustand", "NGINX", "Microfrontends"],
    },
    {
      company: "Freelance Developer",
      role: "Full Stack Developer",
      period: "2021 - 2022",
      description: "Delivered end-to-end web solutions for startups and local businesses across multiple industries.",
      highlights: [
        "Built custom SaaS POS system for restaurants, reducing order processing time by 40%",
        "Designed and implemented REST APIs with Node.js and Express, serving 15+ active clients",
        "Architected PostgreSQL databases with optimized schemas and query performance",
        "Deployed and maintained Docker containerized applications on cloud infrastructure"
      ],
      technologies: ["React", "Node.js", "PostgreSQL", "Express", "Docker"],
    },
  ];

  return (
    <section id="experience" className="py-24">
      <div className="container mx-auto px-6">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-bold mb-12 text-center"
        >
          Work <span className="text-gradient">Experience</span>
        </motion.h2>

        <div className="max-w-4xl mx-auto space-y-8">
          {experiences.map((exp, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              className="relative pl-8 pb-8 border-l-2 border-border last:pb-0"
            >
              <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-primary glow-effect" />
              
              <div className="bg-card p-6 rounded-lg border border-border card-shadow hover:border-primary transition-colors">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-primary" />
                      {exp.role}
                    </h3>
                    <p className="text-primary font-semibold">{exp.company}</p>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground mt-2 md:mt-0">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">{exp.period}</span>
                  </div>
                </div>
                
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  {exp.description}
                </p>

                <ul className="space-y-2 mb-4">
                  {exp.highlights.map((highlight, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="flex flex-wrap gap-2">
                  {exp.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-secondary text-xs font-mono rounded border border-border"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
